<script setup>
import Header from '@/components/Header.vue'
import Footer from '@/components/Footer.vue'
import Helmet from '@/components/Helmet.vue'
import { useRouter, useRoute } from 'vue-router'

const router = useRouter()
const route = useRoute()

function goToContact() {
  if (route.path === '/') {
    // Si ya estás en la página principal, simplemente agregamos el hash
    router.replace({ hash: '#contact' })
  } else {
    // Si estás en otra ruta, redirige a la home con el hash
    router.push({ path: '/', hash: '#contact' })
  }
}
</script>

<template>
  <Helmet
    title="Cómo una página optimizada mejora tu posicionamiento en Google | Resi Corporation"
    description="Aprendé cómo optimizar tu sitio web para aparecer en los primeros lugares de Google y aumentar tus ventas. Velocidad, SEO técnico, contenido, backlinks y más."
  />
  <Header />

  <section class="container">
    <h2 class="title">Cómo una página optimizada mejora tu posicionamiento en Google</h2>

    <p class="summary">
      Descubrí los factores que Google tiene en cuenta para posicionar tu sitio web y por qué la optimización técnica y de contenido puede marcar la diferencia entre estar en la primera página o desaparecer en los resultados.
    </p>

    <div class="meta">
      <span class="author">Por Máximo Felipe Resi</span>
      <span class="date">9 de junio de 2025</span>
    </div>

    <div class="image-wrapper">
      <img src="../../public/img/optimizacionweb.jpg" alt="Gráfico de ranking SEO" title="Optimización SEO y ranking" />
    </div>

    <article class="content">
      <p>
        Tener una página web es apenas el primer paso. Si no está optimizada, es muy probable que Google no la muestre a tus potenciales clientes. Esto significa menos visitas, menos ventas y menos visibilidad. A continuación te contamos qué aspectos prioriza Google en su algoritmo y cómo podés mejorar tu sitio para escalar posiciones.
      </p>

      <h2>⚡ Velocidad de carga: lo primero que Google analiza</h2>
      <p>
        Un sitio lento no solo frustra a los usuarios, también es penalizado por Google. Los sitios que cargan en menos de 2 segundos tienen mayores tasas de retención, mejor posicionamiento y más conversiones.
      </p>
      <p>
        ¿Cómo mejoramos esto?
      </p>
      <ul>
        <li>Minificamos archivos CSS y JavaScript para reducir su peso.</li>
        <li>Usamos imágenes en formato WebP, que son más livianas y conservan calidad.</li>
        <li>Implementamos redes de entrega de contenido (CDN) que cargan los recursos desde servidores cercanos al usuario.</li>
        <li>Hacemos limpieza de plugins innecesarios o pesados.</li>
      </ul>

      <h2>📊 Core Web Vitals: métricas clave de experiencia</h2>
      <p>
        Desde 2021, Google utiliza tres métricas fundamentales que miden la experiencia de usuario en tu sitio:
      </p>
      <ul>
        <li><strong>LCP (Largest Contentful Paint):</strong> mide cuánto tarda en cargarse el contenido más grande de una página. Mejoramos esto optimizando imágenes y el tiempo de respuesta del servidor.</li>
        <li><strong>FID (First Input Delay):</strong> mide la rapidez con la que el sitio responde a la primera interacción del usuario. Lo reducimos cargando scripts de forma diferida y priorizando recursos esenciales.</li>
        <li><strong>CLS (Cumulative Layout Shift):</strong> se refiere a los cambios inesperados de diseño mientras la página carga. Lo corregimos reservando espacios para banners e imágenes desde el HTML.</li>
      </ul>

      <h2>🔧 SEO técnico: facilitá que Google entienda tu web</h2>
      <p>
        El SEO técnico es lo que le permite a los buscadores acceder, rastrear e indexar correctamente tu sitio web. Sin él, incluso el mejor contenido puede quedar invisible.
      </p>
      <ul>
        <li>Usamos estructuras de URL limpias y legibles, separadas por guiones y con palabras clave.</li>
        <li>Incluimos un sitemap en formato .xml para ayudar a Google a encontrar todas las páginas.</li>
        <li>Aplicamos datos estructurados con Schema.org, para enriquecer los resultados de búsqueda con estrellas, precios, reseñas, etc.</li>
        <li>Implementamos redirecciones correctas (301) para evitar errores 404 o contenido duplicado.</li>
      </ul>

      <h2>📝 Contenido relevante: la clave para atraer y retener</h2>
      <p>
        Google prioriza el contenido que resuelve de forma clara y precisa la intención de búsqueda del usuario. Por eso creamos artículos bien investigados, que utilizan palabras clave de forma natural y están organizados con subtítulos, listas y preguntas frecuentes.
      </p>
      <p>
        Además, mantenemos el contenido actualizado, agregamos nuevos temas relacionados cada mes y respondemos a preguntas comunes de tus clientes para posicionarte como autoridad en tu rubro.
      </p>

      <h2>🔗 Autoridad de dominio y backlinks de calidad</h2>
      <p>
        Otro factor decisivo es cuántos sitios web de calidad enlazan al tuyo. Esto le indica a Google que tu contenido es confiable. Para fortalecer tu autoridad:
      </p>
      <ul>
        <li>Buscamos colaboraciones con blogs y medios relevantes (guest posting).</li>
        <li>Creás contenido útil que otras páginas quieran compartir.</li>
        <li>Registramos tu negocio en directorios profesionales.</li>
        <li>Compartimos tus contenidos en redes sociales para generar tráfico externo.</li>
      </ul>

      <h2>📌 Conclusión: optimizar es invertir en resultados</h2>
      <p class="engagement">
        Una web optimizada no solo se ve bien: funciona bien, carga rápido, responde a las necesidades de tus usuarios y cumple con los requisitos técnicos de Google. Todo esto se traduce en más visitas, mejor posicionamiento, más consultas y más ventas. Si querés dejar de depender de redes sociales o intermediarios y tener una fuente estable de clientes, el SEO y la optimización web son esenciales.
      </p>

      <div class="cta-wrapper">
        <a @click.prevent="goToContact" href="#" class="cta-link">Empezá a escalar en Google</a>
      </div>
    </article>
  </section>

  <Footer />
</template>


<style scoped>
.container {
  margin-top: 120px !important;
  max-width: 800px;
  margin: 40px auto;
  padding: 0 20px;
}

.title {
  font-size: 2.4rem;
  font-weight: 700;
  margin-bottom: 10px;
}

.summary {
  font-size: 1.1rem;
  color: var(--text-color);
  margin-bottom: 20px;
  line-height: 1.6;
}

.meta {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  color: var(--text-color);
  margin-bottom: 25px;
  border-bottom: 1px solid #ddd;
  padding-bottom: 10px;
}

.image-wrapper {
  margin-bottom: 30px;
}

.image-wrapper img {
  width: 100%;
  border-radius: 8px;
  object-fit: cover;
}

.content {
  font-size: 1.05rem;
  line-height: 1.8;
  color: var(--text-color);
}

.content h2 {
  font-size: 1.6rem;
  margin-top: 30px;
  margin-bottom: 15px;
}

.content p,
.content ul {
  margin-bottom: 20px;
}

.content ul {
  padding-left: 1.5rem;
}

.content ul li {
  margin-bottom: 0.7rem;
  line-height: 1.5;
}

.engagement {
  color: var(--title-color);
  font-weight: 600;
}

.cta-wrapper {
  text-align: center;
  margin-top: 30px;
}

.cta-link {
  display: inline-block;
  margin-top: 1rem;
  background: var(--first-color);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  transition: background 0.3s ease;
}

.cta-link:hover {
  background: red;
}
</style>
